import { useSyncExternalStore } from "react";
import type { ThemeName } from "../preset/palette.ts";

/** Attribute on `<html>` that selects the theme (ADR 0009, design D22). */
export const THEME_ATTRIBUTE = "data-theme";
/** Browser cache of the last applied theme, read by `/theme-init.js` before the first paint. */
export const THEME_STORAGE_KEY = "strust.theme";
export const DEFAULT_THEME: ThemeName = "dark";

export function isThemeName(value: unknown): value is ThemeName {
  return value === "dark" || value === "light";
}

/** The theme on `<html>` (dark when the attribute is missing or unknown). */
export function readDocumentTheme(): ThemeName {
  const value = document.documentElement.getAttribute(THEME_ATTRIBUTE);
  return isThemeName(value) ? value : DEFAULT_THEME;
}

/** Applies `theme` to `<html>` and remembers it for the next page load in this browser. */
export function applyDocumentTheme(theme: ThemeName): void {
  document.documentElement.setAttribute(THEME_ATTRIBUTE, theme);
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // storage blocked (private mode): the theme still applies to this page
  }
}

function subscribe(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: [THEME_ATTRIBUTE],
  });
  return () => {
    observer.disconnect();
  };
}

/** Subscribes to the theme on `<html>` (for code that cannot use CSS, such as map styles). */
export function useDocumentTheme(): ThemeName {
  return useSyncExternalStore(subscribe, readDocumentTheme, () => DEFAULT_THEME);
}
